import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Empty, Modal, Typography, message } from 'antd';
import WatercolorLoader from '../components/WatercolorLoader';
import { DeleteOutlined, RollbackOutlined } from '@ant-design/icons';
import {
  deleteProjectFully,
  listArchivedProjects,
  restoreProject,
} from '../illustration/api';

const { Title, Text, Paragraph } = Typography;

export default function ArchivedProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listArchivedProjects();
      setProjects(data.projects || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Archived projects · Illustration Studio';
    refresh();
  }, [refresh]);

  const onRestore = async (project) => {
    setBusyId(project.id);
    try {
      await restoreProject(project.id);
      message.success(`Restored “${project.title}”`);
      window.dispatchEvent(new Event('artificiallly:projects-changed'));
      await refresh();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = (project) => {
    Modal.confirm({
      title: `Delete “${project.title}” fully?`,
      content:
        'This removes the project folder from disk (content/projects). This cannot be undone.',
      okText: 'Delete fully',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setBusyId(project.id);
        try {
          await deleteProjectFully(project.id);
          message.success(`Deleted “${project.title}”`);
          window.dispatchEvent(new Event('artificiallly:projects-changed'));
          await refresh();
        } catch (e) {
          message.error(e.message);
          throw e;
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className="workflow-shell__content">
      <Title
        level={1}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-display)',
          letterSpacing: 'var(--tracking-display)',
          marginBottom: 8,
        }}
      >
        Archived projects
      </Title>
      <Paragraph type="secondary" style={{ maxWidth: 'var(--measure-prose)' }}>
        Restore a project to the active list, or delete it fully from disk. Hard delete is only
        available here after archive.
      </Paragraph>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <Link to="/" style={{ color: 'var(--color-text-secondary)' }}>
          ← Back to Illustration Studio
        </Link>
      </div>

      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}

      {loading ? (
        <div className="watercolor-loader-slot">
          <WatercolorLoader />
        </div>
      ) : !projects.length ? (
        <Empty description="No archived projects" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {projects.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: 'block' }}>{p.title}</Text>
                <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
                  Archived {p.archivedAt ? new Date(p.archivedAt).toLocaleString() : '—'}
                  {p.description ? ` · ${p.description}` : ''}
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  icon={<RollbackOutlined />}
                  loading={busyId === p.id}
                  onClick={() => onRestore(p)}
                >
                  Restore
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={busyId === p.id}
                  onClick={() => onDelete(p)}
                >
                  Delete fully
                </Button>
                <Button type="link" onClick={() => navigate(`/projects/${p.id}/settings`)}>
                  View
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
